import type { DashboardTileId } from './dashboard.types';

export type DashboardAccessReason = 'role' | 'subscription' | 'entitlement';

export interface DashboardTileAvailability {
  id: DashboardTileId;
  available: boolean;
  reasons: DashboardAccessReason[];
}

interface DashboardAccess {
  canViewDevices: boolean;
  canViewDeviceDetails: boolean;
  canViewIncidents: boolean;
  canInviteUsers: boolean;
  canViewAuditTrail: boolean;
  canReadStabilityReports: boolean;
  canReadAuditTrailReports: boolean;
  hasSampleSafetyPlus: boolean;
  hasReportHubAccess: boolean;
  showRecentDeviations: boolean;
}

export function dashboardAvailability(
  access: DashboardAccess,
): DashboardTileAvailability[] {
  const tile = (
    id: DashboardTileId,
    requirements: [DashboardAccessReason, boolean][],
  ): DashboardTileAvailability => {
    const reasons = requirements
      .filter(([, granted]) => !granted)
      .map(([reason]) => reason);
    return { id, available: reasons.length === 0, reasons };
  };

  return [
    tile('labHealth', [['role', access.canViewDevices]]),
    tile('deviceOverview', [
      ['role', access.canViewDevices && access.canViewDeviceDetails],
    ]),
    ...(access.showRecentDeviations
      ? [tile('recentDeviations', [['role', access.canViewDeviceDetails]])]
      : []),
    tile('incidents', [['role', access.canViewIncidents]]),
    tile('auditTrail', [
      ['subscription', access.hasSampleSafetyPlus],
      ['role', access.canViewAuditTrail],
    ]),
    tile('pendingInvitations', [['role', access.canInviteUsers]]),
    tile('reports', [
      ['subscription', access.hasSampleSafetyPlus],
      ['entitlement', access.hasReportHubAccess],
      ['role', access.canReadStabilityReports || access.canReadAuditTrailReports],
    ]),
  ];
}
